/**
 * HubSpotQuoteForm
 *
 * Embeds the HubSpot V4 developer Request Quote form and pre-populates the
 * user-identifying fields (design name, first/last name, email, company) once
 * the form has rendered. Exposes a `submit(message)` method via ref so the
 * wrapper can inject the equipment list and trigger HubSpot's submission.
 *
 * The HubSpot bootstrap script is loaded once via public/index.html. It
 * watches the DOM for `.hs-form-html` elements; when this component mounts
 * one, the script renders the form into it and dispatches
 * `hs-form-event:on-ready` on window. On successful submission HubSpot
 * dispatches `hs-form-event:on-submission:success`, which closes the modal
 * via the `onSubmitSuccess` callback.
 */
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { Box, styled } from '@mui/material';
import { useSelector } from 'react-redux';
import { getCurrentValue } from 'reduxModules/ducks/container';
import { useAuth } from '../../contexts/AuthContext';
import {
  HUBSPOT_PORTAL_ID,
  HUBSPOT_FORM_ID,
  HUBSPOT_REGION,
} from '../../config/hubSpot';

// HubSpot field names use a `0-1/` prefix. Callers reference fields by
// symbolic name rather than the raw string.
const FIELD = {
  designName: '0-1/design_tool_project_name',
  firstName: '0-1/firstname',
  lastName: '0-1/lastname',
  email: '0-1/email',
  company: '0-1/company',
  message: '0-1/message',
} as const;

type HubSpotFormInstance = {
  setFieldValue?: (name: string, value: string) => void;
} | null;

interface OidcProfile {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
}

export interface HubSpotQuoteFormHandle {
  submit: (message: string) => void;
}

interface HubSpotQuoteFormProps {
  formRef?: React.Ref<HubSpotQuoteFormHandle>;
  onSubmitSuccess?: () => void;
  onSubmitError?: () => void;
}

/**
 * HubSpot's V4 form renders React-controlled inputs that ignore direct
 * `input.value = x` assignment — React overwrites it on the next render. To
 * persist a value, write it via the prototype's native setter and dispatch
 * input + change events so React picks it up.
 *
 * `form.setFieldValue` keeps HubSpot's internal state in sync alongside the
 * DOM value. It's critical for hidden fields (the multipart payload is built
 * from internal state) and harmless for visible ones.
 */
const writeFieldValue = (
  container: HTMLElement,
  form: HubSpotFormInstance,
  name: string,
  value: string,
) => {
  const input = container.querySelector<HTMLInputElement>(
    `input[name="${name}"], textarea[name="${name}"]`,
  );
  if (!input) return;
  const nativeSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  )?.set;
  nativeSetter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  form?.setFieldValue?.(name, value);
};

const overrideDesignNameLabel = (container: HTMLElement) => {
  const labelStrong = container
    .querySelector<HTMLInputElement>(`input[name="${FIELD.designName}"]`)
    ?.closest('.hsfc-Row')
    ?.querySelector('.hsfc-FieldLabel strong');
  if (labelStrong) labelStrong.textContent = 'Design Name';
};

const buildPrefill = (
  designName: string,
  profile: OidcProfile,
): Record<string, string> => {
  const fullName = profile.name || '';
  const [fallbackFirst, ...fallbackRest] = fullName.split(/\s+/);
  return {
    [FIELD.designName]: designName,
    [FIELD.firstName]: profile.given_name || fallbackFirst || '',
    [FIELD.lastName]: profile.family_name || fallbackRest.join(' ') || '',
    [FIELD.email]: profile.email || '',
    [FIELD.company]: sessionStorage.getItem('selectedOrgName') || '',
  };
};

const createEmbedDiv = () => {
  const div = document.createElement('div');
  div.className = 'hs-form-html';
  div.setAttribute('data-region', HUBSPOT_REGION);
  div.setAttribute('data-form-id', HUBSPOT_FORM_ID);
  div.setAttribute('data-portal-id', HUBSPOT_PORTAL_ID);
  return div;
};

const HubSpotQuoteForm: React.FC<HubSpotQuoteFormProps> = ({
  formRef,
  onSubmitSuccess,
  onSubmitError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const formInstanceRef = useRef<HubSpotFormInstance>(null);
  const prefillRef = useRef<Record<string, string>>({});
  const onSubmitSuccessRef = useRef<typeof onSubmitSuccess>(undefined);
  const onSubmitErrorRef = useRef<typeof onSubmitError>(undefined);

  const designName = useSelector(
    (state: any) => getCurrentValue(state, 'name'),
  ) as string | undefined;
  const { user } = useAuth();

  // HubSpot's bootstrap takes ~1-2s. During that window the user profile or
  // design name may still be loading. Mirroring the latest values into a ref
  // lets the pre-pop timer read whatever's current when it fires, rather than
  // closing over stale values from when the embed first mounted.
  useEffect(() => {
    prefillRef.current = buildPrefill(
      designName || '',
      (user?.profile || {}) as OidcProfile,
    );
  }, [designName, user]);

  // Keep the success/error callback refs in sync so the mount-only effect
  // always calls the latest versions.
  useEffect(() => {
    onSubmitSuccessRef.current = onSubmitSuccess;
  }, [onSubmitSuccess]);
  useEffect(() => {
    onSubmitErrorRef.current = onSubmitError;
  }, [onSubmitError]);

  useImperativeHandle(
    formRef,
    () => ({
      submit: (message: string) => {
        const container = containerRef.current;
        if (!container) return;
        writeFieldValue(container, formInstanceRef.current, FIELD.message, message);
        // HubSpot's own Submit button is CSS-hidden but still in the DOM —
        // its click handler is what wires up validation + submission, so we
        // trigger that rather than calling a form.submit() that would skip
        // HubSpot's validation gate.
        const nativeSubmit = container.querySelector<HTMLButtonElement>(
          'button[type="submit"], button.hsfc-Button',
        );
        nativeSubmit?.click();
      },
    }),
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleReady = (event: Event) => {
      const form = window.HubSpotFormsV4?.getFormFromEvent?.(event) || null;
      formInstanceRef.current = form;

      // setFieldValue updates HubSpot's internal React state; their next
      // render writes the value into the DOM. No timing race because we're
      // not fighting their render — we're feeding it.
      Object.entries(prefillRef.current).forEach(([name, value]) => {
        if (value) form?.setFieldValue?.(name, value);
      });
      overrideDesignNameLabel(container);
    };

    const handleSuccess = () => {
      onSubmitSuccessRef.current?.();
      // Detach after first success to prevent duplicate handling (per PIG-418).
      window.removeEventListener('hs-form-event:on-submission:success', handleSuccess);
    };

    const handleError = (event: Event) => {
      console.error('HubSpot quote submission failed', event);
      onSubmitErrorRef.current?.();
    };

    // Register listeners BEFORE the embed div lands in the DOM — otherwise the
    // events can fire before we're listening for them. HubSpot's V4 error
    // ; we subscribe to both likely names and keep whichever fires during testing.
    const listeners: Array<[string, EventListener]> = [
      ['hs-form-event:on-ready', handleReady],
      ['hs-form-event:on-submission:success', handleSuccess],
      ['hs-form-event:on-submission:error', handleError],
      ['hs-form-event:on-submission:fail', handleError],
    ];
    listeners.forEach(([name, handler]) => window.addEventListener(name, handler));
    container.appendChild(createEmbedDiv());

    return () => {
      listeners.forEach(([name, handler]) => window.removeEventListener(name, handler));
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      formInstanceRef.current = null;
    };
  }, []);

  return <StyledFormContainer ref={containerRef} />;
};

/**
 * CSS overrides for HubSpot's V4 form (`.hsfc-*` classes), reproducing the
 * look of the existing QuoteForm / Input / InputLabel atoms so the embed
 * doesn't visually break from the rest of the app.
 */
const StyledFormContainer = styled(Box)(({ theme }) => ({
  // Zero out HubSpot's background padding (it applies via these CSS vars on
  // an internal wrapper — overriding the variable kills the padding regardless
  // of which descendant element reads it).
  '--hsf-background__padding': '0px',
  '--hsf-default-background__padding': '0px',

  width: '100%',
  backgroundColor: theme.palette.background.paper,
  padding: 0,

  // Stretch HubSpot's inner wrappers to fill the modal body
  '& .hsfc-Renderer, & .hsfc-FormWrapper': {
    width: '100%',
  },

  '& .hsfc-Step__Content': {
    padding: '10px',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '10px',
    columnGap: '16px',
  },

  '& .hsfc-Row': {
    flex: '1 1 calc(50% - 8px)',
    minWidth: 0,
  },

  '& .hsfc-TextField, & .hsfc-EmailField, & .hsfc-PhoneField, & .hsfc-DropdownField': {
    width: '100%',
  },

  '& .hsfc-Row:has(input[name="0-1/design_tool_project_name"]), & .hsfc-Row:has(.hsfc-DropdownField)': {
    flex: '1 1 100%',
  },

  '& .hsfc-Row:has(input[name="0-1/message"])': {
    display: 'none',
  },

  '& .hsfc-FieldLabel': {
    marginBottom: '5px',
    display: 'inline-block',
  },

  '& .hsfc-FieldLabel strong': {
    fontFamily: '"Open Sans"',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: 'normal',
    letterSpacing: '-0.24px',
    color: theme.palette.text.primary,
  },
  '& .hsfc-FieldLabel__RequiredIndicator': {
    color: theme.palette.error.main,
    fontWeight: 400,
    marginLeft: '2px',
  },

  '& .hsfc-TextInput': {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.default,
    borderRadius: '4px',
    boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.05)',
    borderWidth: '0.6px',
    borderStyle: 'solid',
    borderColor: theme.palette.mode === 'light'
      ? 'rgba(17, 17, 17, 0.15)'
      : theme.palette.divider,
    fontFamily: '"Open Sans"',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '130%',
    letterSpacing: '-0.24px',
    color: theme.palette.text.primary,
    padding: '8px 12px',
    transition: 'border-color 0.2s',

    '&:hover': {
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(17, 17, 17, 0.25)'
        : theme.palette.action.hover,
    },
    '&:focus, &:focus-visible': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
      outline: 'none',
    },
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
  },

  '& .hsfc-TextInput--button': {
    textAlign: 'left',
    cursor: 'pointer',
  },

  // Inline per-field validation error
  '& .hsfc-ErrorAlert': {
    fontFamily: '"Open Sans"',
    fontSize: '12px',
    color: theme.palette.error.main,
    marginTop: '4px',
    marginLeft: '12px',
  },

  // Modal supplies its own Submit button — hide HubSpot's native one
  '& .hsfc-Button': { display: 'none !important' },
  '& .hsfc-NavigationRow': { display: 'none !important' },
  // Suppress HubSpot's post-submit thank-you screen flash
  '& .hsfc-PostSubmit': { display: 'none !important' },
}));

declare global {
  interface Window {
    HubSpotFormsV4?: {
      getFormFromEvent?: (event: Event) => HubSpotFormInstance;
      getForms?: () => unknown[];
    };
  }
}

export default HubSpotQuoteForm;
